<template>
  <Dialog
    :visible="true"
    modal
    header="Emergency contact required"
    :style="{ width: '28rem' }"
    :breakpoints="{ '575px': 'calc(100vw - 2rem)' }"
    :closable="false"
    :close-on-escape="false"
  >
    <!-- The Save button in the footer submits this form, so Enter saves -->
    <form
      id="required-emergency-form"
      class="kb-form"
      novalidate
      @submit.prevent="onSave"
    >
      <p class="kb-dialog-lead">
        Please provide an emergency contact. This information will only be used in case of an emergency during parade day.
      </p>

      <KbField
        v-slot="{ describedBy }"
        field-id="emergency-name"
        label="Emergency contact name"
        :error="nameError"
      >
        <InputText
          id="emergency-name"
          v-model="emergencyName"
          size="small"
          fluid
          placeholder="Enter contact name"
          :autofocus="focusField === 'name'"
          :invalid="Boolean(nameError)"
          :aria-describedby="describedBy"
        />
      </KbField>

      <KbField
        v-slot="{ describedBy }"
        field-id="emergency-phone"
        label="Emergency contact phone"
        :error="phoneError"
      >
        <KbPhoneInput
          id="emergency-phone"
          v-model="emergencyPhone"
          :autofocus="focusField === 'phone'"
          :invalid="Boolean(phoneError)"
          :aria-describedby="describedBy"
        />
      </KbField>

      <KbField
        v-slot="{ describedBy }"
        field-id="emergency-relationship"
        label="Relationship"
        optional
      >
        <InputText
          id="emergency-relationship"
          v-model="emergencyRelationship"
          size="small"
          fluid
          placeholder="e.g., Spouse, Parent, Friend"
          :aria-describedby="describedBy"
        />
      </KbField>
      <p
        v-if="saveError"
        class="kb-callout-alert m-0"
        role="alert"
      >
        {{ saveError }}
      </p>
    </form>

    <template #footer>
      <Button
        type="submit"
        form="required-emergency-form"
        label="Save"
        size="small"
        :loading="saving"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { Member } from '~/shared/types/member'
import { isValidPhone, normalizePhone } from '~/utils/phone'

const props = defineProps<{
  member: Member
}>()

const membersStore = useMembersStore()

const saving = ref(false)
const submitted = ref(false)
const saveError = ref('')

// Pre-fill with existing data if available (check new array format first, then legacy)
const existingContact = props.member.emergency_contacts?.[0] || props.member.emergency_contact
const emergencyName = ref(existingContact?.name || '')
// KbPhoneInput keeps this in the stored form ("+15045551234") once edited
const emergencyPhone = ref<string | null | undefined>(existingContact?.phone || '')
const emergencyRelationship = ref(existingContact?.relationship || '')

// Focus starts in the first field that still needs filling in
const focusField = !emergencyName.value.trim() || isValidPhone(emergencyPhone.value) ? 'name' : 'phone'

const nameError = computed(() =>
  submitted.value && !emergencyName.value.trim() ? 'Emergency contact name is required' : undefined)

// The app's phone rule (utils/phone.ts): 11 to 15 digits with the country
// code; a bare 10-digit number counts as US/Canada
const phoneError = computed(() => {
  if (!submitted.value) return undefined
  if (!normalizePhone(emergencyPhone.value)) return 'Emergency contact phone is required'
  if (!isValidPhone(emergencyPhone.value)) return 'Enter the full phone number'
  return undefined
})

const onSave = async () => {
  if (saving.value) return
  submitted.value = true

  if (!emergencyName.value.trim() || !isValidPhone(emergencyPhone.value)) {
    return
  }

  saving.value = true
  try {
    // Stored as "+" and digits only
    const contact: { name: string, phone: string, relationship?: string } = {
      name: emergencyName.value.trim(),
      phone: normalizePhone(emergencyPhone.value),
    }
    if (emergencyRelationship.value.trim()) {
      contact.relationship = emergencyRelationship.value.trim()
    }

    // Save to new array format, preserving any additional contacts
    const existingContacts = props.member.emergency_contacts || []
    const newContacts = existingContacts.length > 0
      ? [contact, ...existingContacts.slice(1)] // Replace first, keep rest
      : [contact]

    saveError.value = ''
    const saved = await membersStore.updateMember(props.member, {
      emergency_contact: contact, // Keep legacy field for backward compatibility
      emergency_contacts: newContacts,
    })
    if (!saved) {
      saveError.value = 'Your details weren\'t saved. Please try again.'
    }
    // Dialog will auto-close when memberNeedsEmergencyContact becomes false
  }
  finally {
    saving.value = false
  }
}
</script>
